
// Resolves which personas should be applied to which categories and markets based on personaSetting flags.
export function resolvePersonaScope({
  categories,
  markets,
  personas,
  primaryCategory,
  primaryMarket,
  primaryPersona,
  personaSetting = {},
}) {
  const applyAllCategory = !!personaSetting?.applyPersonaToAllCategory;
  const applyAllMarkets = !!personaSetting?.applyPersonaToAllMarkets;

  // Structure: scopeMap[category][market] = [personas to apply]
  const scopeMap = {};

  // Initialize all categories
  categories.forEach(category => {
    scopeMap[category] = {};
  });

  // Case 4: All personas everywhere
  if (applyAllCategory && applyAllMarkets) {
    categories.forEach(category => {
      markets.forEach(market => {
        scopeMap[category][market] = [...personas];
      });
    });
    return scopeMap;
  }

  // Case 1: Both restricted (default)
  if (!applyAllCategory && !applyAllMarkets) {
    categories.forEach(category => {
      markets.forEach(market => {
        const isPrimaryCategory = category === primaryCategory;
        const isPrimaryMarket = market === primaryMarket;

        if (isPrimaryCategory && isPrimaryMarket) {
          // Primary category + primary market = all personas
          scopeMap[category][market] = [...personas];
        } else {
          // Everything else = only primary persona
          scopeMap[category][market] = [primaryPersona];
        }
      });
    });
    return scopeMap;
  }

  // Case 2: All categories, restricted market
  if (applyAllCategory && !applyAllMarkets) {
    categories.forEach(category => {
      markets.forEach(market => {
        if (market === primaryMarket) {
          // Primary market = all personas for all categories
          scopeMap[category][market] = [...personas];
        } else {
          // Other markets = only primary persona
          scopeMap[category][market] = [primaryPersona];
        }
      });
    });
    return scopeMap;
  }

  // Case 3: All markets, restricted category
  if (!applyAllCategory && applyAllMarkets) {
    categories.forEach(category => {
      markets.forEach(market => {
        if (category === primaryCategory) {
          // Primary category = all personas for all markets
          scopeMap[category][market] = [...personas];
        } else {
          // Other categories = only primary persona
          scopeMap[category][market] = [primaryPersona];
        }
      });
    });
    return scopeMap;
  }

  return scopeMap;
}

// Filters personas array based on the scope map for a specific category-market pair.Used during combination generation to determine which personas are valid.
export function getPersonasForScope({ scopeMap, category, market, allPersonas }) {
  if (!scopeMap || !scopeMap[category] || !scopeMap[category][market]) {
    // Fallback: return all personas if scope map is not available
    return allPersonas;
  }

  return scopeMap[category][market];
}

export function validatePersonaSelection({
  selectedCategories,
  selectedMarkets,
  selectedPersonas,
  primaryCategory,
  primaryMarket,
  primaryPersona,
  personaSetting = {},
}) {
  const applyAllCategory = !!personaSetting?.applyPersonaToAllCategory;
  const applyAllMarkets = !!personaSetting?.applyPersonaToAllMarkets;

  // If only primary persona is selected, always valid
  if (selectedPersonas.length === 1 && selectedPersonas[0] === primaryPersona) {
    return { isValid: true, invalidReason: null };
  }

  // If multiple personas are selected, check against the flags
  const hasMultiplePersonas = selectedPersonas.length > 1 ||
    (selectedPersonas.length === 1 && selectedPersonas[0] !== primaryPersona);

  if (!hasMultiplePersonas) {
    return { isValid: true, invalidReason: null };
  }

  // Case 4: All personas everywhere - VALID
  if (applyAllCategory && applyAllMarkets) {
    return { isValid: true, invalidReason: null };
  }

  // Case 1: Both restricted - only primary category + primary market can have multiple personas
  if (!applyAllCategory && !applyAllMarkets) {
    const hasOnlyPrimaryCategory = selectedCategories.length === 1 &&
      selectedCategories[0] === primaryCategory;
    const hasOnlyPrimaryMarket = selectedMarkets.length === 1 &&
      selectedMarkets[0] === primaryMarket;

    if (hasOnlyPrimaryCategory && hasOnlyPrimaryMarket) {
      return { isValid: true, invalidReason: null };
    }

    return {
      isValid: false,
      invalidReason: `Multiple personas can only be applied to primary category (${primaryCategory}) and primary market (${primaryMarket}) when both flags are not check.`,
    };
  }

  // Case 2: All categories, restricted market - only primary market can have multiple personas
  if (applyAllCategory && !applyAllMarkets) {
    const hasOnlyPrimaryMarket = selectedMarkets.length === 1 &&
      selectedMarkets[0] === primaryMarket;

    if (hasOnlyPrimaryMarket) {
      return { isValid: true, invalidReason: null };
    }

    return {
      isValid: false,
      invalidReason: `Multiple personas can only be applied to primary market (${primaryMarket}) when "Apply to All Markets" is not check.`,
    };
  }

  // Case 3: All markets, restricted category - only primary category can have multiple personas
  if (!applyAllCategory && applyAllMarkets) {
    const hasOnlyPrimaryCategory = selectedCategories.length === 1 &&
      selectedCategories[0] === primaryCategory;

    if (hasOnlyPrimaryCategory) {
      return { isValid: true, invalidReason: null };
    }

    return {
      isValid: false,
      invalidReason: `Multiple personas can only be applied to primary category (${primaryCategory}) when "Apply to All Categories" is not check.`,
    };
  }

  return { isValid: true, invalidReason: null };
}
